import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ExternalLink } from "lucide-react";
import { api, money } from "../lib/api";

const EMPTY = {
  name: "",
  description: "",
  price: "",
  category: "Seating",
  subcategory: "",
  images: [""],
  material: "",
  dimensions: "",
  colors_text: "",
  sizes_text: "",
  size_label: "Size",
  color_images_text: {},
  supplier_url: "",
  stock: 100,
  featured: false,
};

const splitList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

// Parse "Small:250, Large:400" -> { sizes:["Small","Large"], size_prices:{Small:250,Large:400} }
const parseSizes = (s) => {
  const sizes = [];
  const size_prices = {};
  splitList(s).forEach((token) => {
    const [namePart, pricePart] = token.split(":").map((x) => x.trim());
    if (!namePart) return;
    sizes.push(namePart);
    const price = parseFloat(pricePart);
    if (!Number.isNaN(price)) size_prices[namePart] = price;
  });
  return { sizes, size_prices };
};

// Rebuild "Small:250, Large" text from stored sizes + size_prices
const sizesToText = (sizes, size_prices) =>
  (sizes || [])
    .map((name) => (size_prices && size_prices[name] != null ? `${name}:${size_prices[name]}` : name))
    .join(", ");

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(null);

  const [importUrls, setImportUrls] = useState("");
  const [importing, setImporting] = useState(false);
  const [importJob, setImportJob] = useState(null);
  const [productSearch, setProductSearch] = useState("");

  const loadProducts = () => api.get("/admin/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data));

  useEffect(() => {
    loadProducts();
    loadOrders();
  }, []);

  const publish = async (id) => {
    await api.post(`/admin/products/${id}/publish`);
    toast.success("Published — now live on the store");
    loadProducts();
  };

  const startImport = async () => {
    const urls = importUrls.split(/[\n,\s]+/).map((u) => u.trim()).filter((u) => u.startsWith("http"));
    if (urls.length === 0) {
      toast.error("Paste at least one product URL (one per line).");
      return;
    }
    setImporting(true);
    setImportJob(null);
    try {
      const { data } = await api.post("/admin/bulk-import", { urls });
      pollImport(data.job_id);
    } catch (e) {
      setImporting(false);
      toast.error(e.response?.data?.detail || "Could not start import");
    }
  };

  const pollImport = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/import-jobs/${jobId}`);
      setImportJob(data);
      if (data.status === "completed") {
        setImporting(false);
        loadProducts();
        const ok = data.items.filter((i) => i.status === "done").length;
        toast.success(`Imported ${ok}/${data.total} as drafts — review them in Products`);
        return;
      }
    } catch {
      /* keep polling */
    }
    setTimeout(() => pollImport(jobId), 3000);
  };

  const save = async (e) => {
    e.preventDefault();
    const images = (form.images || []).map((u) => u.trim()).filter(Boolean);
    if (images.length === 0) {
      toast.error("Add at least one image URL.");
      return;
    }
    const { sizes, size_prices } = parseSizes(form.sizes_text);
    const colors = splitList(form.colors_text);
    const color_images = {};
    colors.forEach((c) => {
      const urls = splitList((form.color_images_text || {})[c]);
      if (urls.length) color_images[c] = urls;
    });
    const payload = {
      ...form,
      price: parseFloat(form.price),
      stock: parseInt(form.stock) || 0,
      images,
      image: images[0],
      colors,
      sizes,
      size_prices,
      color_images,
      size_label: (form.size_label || "Size").trim() || "Size",
    };
    delete payload.colors_text;
    delete payload.sizes_text;
    delete payload.color_images_text;
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Product saved");
      setForm(null);
      loadProducts();
    } catch {
      toast.error("Could not save product");
    }
  };

  const setImage = (idx, val) =>
    setForm((f) => ({ ...f, images: f.images.map((u, i) => (i === idx ? val : u)) }));
  const addImage = () => setForm((f) => ({ ...f, images: [...(f.images || []), ""] }));
  const removeImage = (idx) =>
    setForm((f) => {
      const next = f.images.filter((_, i) => i !== idx);
      return { ...f, images: next.length ? next : [""] };
    });

  const openEdit = (p) =>
    setForm({
      ...p,
      price: String(p.price),
      subcategory: p.subcategory || "",
      images: p.images && p.images.length ? p.images : p.image ? [p.image] : [""],
      colors_text: (p.colors || []).join(", "),
      sizes_text: sizesToText(p.sizes, p.size_prices),
      size_label: p.size_label || "Size",
      supplier_url: p.supplier_url || "",
      color_images_text: Object.fromEntries(
        Object.entries(p.color_images || {}).map(([k, v]) => [k, (v || []).join(", ")])
      ),
    });

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));
  const categoryOptions = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();
  const subcategoryOptions = [...new Set(products.map((p) => p.subcategory).filter(Boolean))].sort();
  const filteredProducts = products.filter((p) => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return true;
    return `${p.name} ${p.category} ${p.subcategory || ""}`.toLowerCase().includes(q);
  });

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    loadProducts();
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-sm uppercase tracking-luxe text-[#999] mb-3">Dashboard</div>
          <h1 className="font-serif text-5xl tracking-tight">Admin</h1>
        </div>
        {tab === "products" && (
          <button
            data-testid="add-product-btn"
            onClick={() => setForm({ ...EMPTY })}
            className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-6 py-3 text-sm uppercase tracking-luxe hover:bg-[#333]"
          >
            <Plus size={16} /> New Product
          </button>
        )}
      </div>

      <div className="flex gap-6 border-b border-[#e5e5e5] mb-10">
        {["products", "import", "orders"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`pb-4 text-sm uppercase tracking-luxe -mb-px border-b-2 ${
              tab === t ? "border-[#1a1a1a]" : "border-transparent text-[#999]"
            }`}
          >
            {t === "products" && `Products (${products.length})`}
            {t === "import" && "Bulk Import"}
            {t === "orders" && `Orders (${orders.length})`}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="overflow-x-auto">
          <div className="mb-6 max-w-sm">
            <input
              data-testid="admin-product-search"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Search products by name or category…"
              className="w-full border border-[#ddd] px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a]"
            />
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#999] uppercase tracking-luxe text-[11px] border-b border-[#e5e5e5]">
                <th className="py-4">Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => (
                <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-b border-[#f0f0f0]">
                  <td className="py-4 flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#f5f5f0] overflow-hidden">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="font-medium">{p.name}</span>
                    {p.draft && (
                      <span data-testid={`draft-badge-${p.id}`} className="text-[10px] uppercase tracking-luxe bg-[#f5d90a] text-[#1a1a1a] px-2 py-0.5">
                        Draft
                      </span>
                    )}
                  </td>
                  <td>{p.category}</td>
                  <td>{money(p.price)}</td>
                  <td>{p.stock}</td>
                  <td className="text-right whitespace-nowrap">
                    {p.draft && (
                      <button onClick={() => publish(p.id)} data-testid={`publish-${p.id}`} className="text-xs uppercase tracking-luxe text-[#1a1a1a] border border-[#1a1a1a] px-3 py-1.5 hover:bg-[#1a1a1a] hover:text-white transition-colors mr-2">
                        Publish
                      </button>
                    )}
                    <button onClick={() => openEdit(p)} className="p-2 hover:text-black text-[#666]" data-testid={`edit-${p.id}`}>
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => remove(p.id)} className="p-2 hover:text-[#b00020] text-[#666]" data-testid={`delete-${p.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "import" && (
        <div data-testid="import-panel" className="max-w-3xl">
          <h2 className="font-serif text-3xl mb-2">Bulk Product Import</h2>
          <p className="text-[#666] text-sm mb-6 leading-relaxed">
            Paste product page URLs from any wholesaler — one per line. Nesta will scrape each page, use AI
            (Claude) to extract the title, images and details and rewrite the description, then add them to your
            catalogue as <strong>drafts</strong> for you to review before publishing.
          </p>
          <textarea
            data-testid="import-urls"
            value={importUrls}
            onChange={(e) => setImportUrls(e.target.value)}
            rows={8}
            placeholder={"https://wholesaler.com/product/sofa-123\nhttps://wholesaler.com/product/chair-456"}
            className="w-full border border-[#ddd] px-4 py-3 text-sm font-mono outline-none focus:border-[#1a1a1a]"
          />
          <div className="flex items-center gap-4 mt-4">
            <button
              data-testid="bulk-import-btn"
              onClick={startImport}
              disabled={importing}
              className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-6 py-3 text-sm uppercase tracking-luxe hover:bg-[#333] disabled:opacity-50"
            >
              {importing ? "Importing…" : "Bulk Import"}
            </button>
            <span className="text-xs text-[#999]">Up to 30 URLs at a time. Runs in the background.</span>
          </div>

          {importJob && (
            <div data-testid="import-progress" className="mt-8 border border-[#e5e5e5] p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm uppercase tracking-luxe text-[#999]">
                  {importJob.status === "completed" ? "Import complete" : "Importing…"}
                </span>
                <span className="text-sm font-medium">{importJob.processed}/{importJob.total}</span>
              </div>
              <div className="space-y-2">
                {importJob.items.map((it, i) => (
                  <div key={i} data-testid={`import-item-${i}`} className="flex items-center justify-between gap-3 text-sm border-b border-[#f0f0f0] pb-2">
                    <span className="truncate text-[#666] flex-1">{it.name || it.url}</span>
                    <span
                      data-testid={`import-item-status-${i}`}
                      className={`text-[11px] uppercase tracking-luxe px-2 py-0.5 shrink-0 ${
                        it.status === "done"
                          ? "bg-[#1a1a1a] text-white"
                          : it.status === "failed"
                          ? "bg-[#fde2e2] text-[#b00020]"
                          : "bg-[#f5f5f0] text-[#999]"
                      }`}
                      title={it.error || ""}
                    >
                      {it.status}
                    </span>
                  </div>
                ))}
              </div>
              {importJob.status === "completed" && (
                <button
                  onClick={() => setTab("products")}
                  className="mt-4 text-sm uppercase tracking-luxe underline"
                >
                  Review drafts in Products →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 && <p className="text-[#666]">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-[#e5e5e5] p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm text-[#999]">{new Date(o.created_at).toLocaleString()}</div>
                  <div>{o.email || "Guest"} · {o.items.length} item(s)</div>
                  {o.shipping && (
                    <div className="text-xs text-[#666] mt-1" data-testid={`admin-order-shipping-${o.id}`}>
                      Ship to: {o.shipping.full_name}, {o.shipping.line1}
                      {o.shipping.line2 ? `, ${o.shipping.line2}` : ""}, {o.shipping.city}, {o.shipping.postal_code}, {o.shipping.country}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-medium">{money(o.amount)}</div>
                  <span className="text-[11px] uppercase tracking-luxe text-[#666]">{o.payment_status}</span>
                </div>
              </div>

              <div className="mt-4 border-t border-[#f0f0f0] pt-3 space-y-2">
                {o.items.map((it, idx) => {
                  const supplierUrl = productMap[it.product_id]?.supplier_url;
                  return (
                    <div key={idx} className="flex items-center justify-between gap-3 text-sm">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-[#f5f5f0] overflow-hidden shrink-0">
                          {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <span className="truncate">
                          {it.name}
                          {(it.color || it.size) && (
                            <span className="text-[#999]"> — {[it.color, it.size].filter(Boolean).join(" / ")}</span>
                          )}
                          <span className="text-[#999]"> × {it.quantity}</span>
                        </span>
                      </div>
                      {supplierUrl ? (
                        <a
                          href={supplierUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`order-item-link-${o.id}-${idx}`}
                          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-luxe text-[#1a1a1a] border border-[#1a1a1a] px-3 py-1.5 hover:bg-[#1a1a1a] hover:text-white transition-colors shrink-0"
                        >
                          <ExternalLink size={13} /> Order
                        </a>
                      ) : (
                        <span className="text-[11px] text-[#bbb] shrink-0">No supplier link</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            data-testid="product-form"
            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl">{form.id ? "Edit" : "New"} Product</h2>
              <button type="button" onClick={() => setForm(null)}><X size={20} /></button>
            </div>
            <p className="text-xs text-[#999] mb-6 -mt-3">
              Only <strong>name</strong>, <strong>price</strong> and <strong>one image</strong> are required — everything else is optional.
            </p>
            <div className="space-y-4">
              {[
                { k: "name", label: "Name" },
                { k: "material", label: "Material" },
                { k: "dimensions", label: "Dimensions" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-xs uppercase tracking-luxe text-[#999]">{f.label}</label>
                  <input
                    data-testid={`form-${f.k}`}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                </div>
              ))}

              <div data-testid="form-images">
                <label className="text-xs uppercase tracking-luxe text-[#999]">
                  Image URLs <span className="text-[#bbb]">(first is the cover)</span>
                </label>
                <div className="space-y-2">
                  {(form.images || []).map((url, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-[#f5f5f0] overflow-hidden shrink-0">
                        {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <input
                        data-testid={`form-image-${idx}`}
                        value={url}
                        onChange={(e) => setImage(idx, e.target.value)}
                        placeholder="https://…"
                        className="flex-1 border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                      />
                      <button
                        type="button"
                        data-testid={`remove-image-${idx}`}
                        onClick={() => removeImage(idx)}
                        className="p-2 text-[#999] hover:text-[#b00020]"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="add-image-btn"
                  onClick={addImage}
                  className="mt-2 inline-flex items-center gap-2 text-sm text-[#1a1a1a] hover:opacity-60"
                >
                  <Plus size={14} /> Add another image
                </button>
              </div>
              <div>
                <label className="text-xs uppercase tracking-luxe text-[#999]">Description</label>
                <textarea
                  data-testid="form-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-luxe text-[#999]">Price</label>
                  <input
                    data-testid="form-price"
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    required
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-luxe text-[#999]">Stock</label>
                  <input
                    data-testid="form-stock"
                    type="number"
                    value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-luxe text-[#999]">Category</label>
                  <input
                    data-testid="form-category"
                    list="category-options"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g. Seating"
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                  <datalist id="category-options">
                    {categoryOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-luxe text-[#999]">
                    Sub-category <span className="text-[#bbb]">(optional)</span>
                  </label>
                  <input
                    data-testid="form-subcategory"
                    list="subcategory-options"
                    value={form.subcategory}
                    onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                    placeholder="e.g. Armchairs"
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                  <datalist id="subcategory-options">
                    {subcategoryOptions.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 border-t border-[#eee] pt-4">
                <div>
                  <label className="text-xs uppercase tracking-luxe text-[#999]">
                    Colours <span className="text-[#bbb]">(comma separated)</span>
                  </label>
                  <input
                    data-testid="form-colors"
                    value={form.colors_text}
                    onChange={(e) => setForm({ ...form, colors_text: e.target.value })}
                    placeholder="Oak, Walnut, Black"
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-luxe text-[#999]">Size label for this item</label>
                  <input
                    data-testid="form-size-label"
                    value={form.size_label}
                    onChange={(e) => setForm({ ...form, size_label: e.target.value })}
                    placeholder="Size / Length / Dimensions"
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-luxe text-[#999]">
                    {(form.size_label || "Size")} options <span className="text-[#bbb]">(comma separated)</span>
                  </label>
                  <input
                    data-testid="form-sizes"
                    value={form.sizes_text}
                    onChange={(e) => setForm({ ...form, sizes_text: e.target.value })}
                    placeholder="Small:250, Medium:320, Large:400"
                    className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                  />
                  <p className="text-[11px] text-[#999] mt-1">
                    Add <code>Name:Price</code> for per-size pricing (e.g. <code>Small:250, Large:400</code>), or just names
                    to use the base price. Leave blank if this item has no colour/size choices.
                  </p>
                </div>
              </div>

              {splitList(form.colors_text).length > 0 && (
                <div data-testid="form-color-images" className="border-t border-[#eee] pt-4">
                  <label className="text-xs uppercase tracking-luxe text-[#999]">
                    Colour photos <span className="text-[#bbb]">(optional — shown when a customer picks that colour)</span>
                  </label>
                  <div className="space-y-2 mt-1">
                    {splitList(form.colors_text).map((c) => (
                      <div key={c} className="flex items-center gap-3">
                        <span className="text-sm w-24 shrink-0 truncate">{c}</span>
                        <input
                          data-testid={`form-color-images-${c}`}
                          value={(form.color_images_text || {})[c] || ""}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              color_images_text: { ...(f.color_images_text || {}), [c]: e.target.value },
                            }))
                          }
                          placeholder="Image URLs for this colour, comma separated"
                          className="flex-1 border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#999] mt-1">
                    Leave a colour blank to use the main images. The first URL for a colour becomes its cover photo.
                  </p>
                </div>
              )}

              <div className="border-t border-[#eee] pt-4">
                <label className="text-xs uppercase tracking-luxe text-[#999]">
                  Supplier / fulfilment link <span className="text-[#bbb]">(admin only — hidden from customers)</span>
                </label>
                <input
                  data-testid="form-supplier-url"
                  value={form.supplier_url}
                  onChange={(e) => setForm({ ...form, supplier_url: e.target.value })}
                  placeholder="https://supplier.com/order/this-item"
                  className="w-full border border-[#e5e5e5] px-3 py-2 outline-none focus:border-[#1a1a1a]"
                />
                <p className="text-[11px] text-[#999] mt-1">
                  Shown on each item in the Orders tab so you can quickly reorder from your supplier. Never visible to customers.
                </p>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="form-featured"
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                />
                Featured on homepage
              </label>
            </div>
            <button
              data-testid="save-product-btn"
              type="submit"
              className="w-full mt-8 bg-[#1a1a1a] text-white py-4 text-sm uppercase tracking-luxe hover:bg-[#333]"
            >
              Save Product
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
